/**
 * WorkerClient - Web Worker 통신 관리 클래스
 *
 * 책임:
 * - Worker 생명주기 관리 (생성, 종료)
 * - 메시지 큐잉 (Worker 준비 전 호출 저장)
 * - 페이지 종료 핸들링 (Beacon 전송)
 */

import type {
  LoggerConfig,
  MainToWorkerMessage,
  WorkerToMainMessage,
} from '../types';

/**
 * iOS 디바이스 감지
 * iOS Safari에서는 pagehide/beforeunload가 발생하지 않을 수 있어
 * visibilitychange에서 Beacon을 사용해야 함
 */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /ipad|iphone|ipod/i.test(navigator.userAgent);
}

const MAX_QUEUE_LENGTH = 1000;

export type WorkerState = 'idle' | 'initializing' | 'ready' | 'failed';

export interface WorkerClientCallbacks {
  onReady?: () => void;
  onError?: (error: string, context?: string) => void;
}

/**
 * Worker 생성 팩토리 타입
 * 앱에서 직접 Worker 생성 로직을 주입해야 합니다.
 *
 * @example
 * ```typescript
 * const workerFactory = () =>
 *   new Worker(new URL('@zep/logger/logger.worker', import.meta.url), {
 *     type: 'module',
 *   });
 * ```
 */
export type WorkerFactory = () => Worker;

export interface WorkerClientOptions {
  callbacks?: WorkerClientCallbacks;
  /** Worker 생성 팩토리 (필수) - Next.js 환경에서 import.meta.url 호환성을 위해 앱에서 주입 */
  workerFactory: WorkerFactory;
}

export class WorkerClient {
  private worker: Worker | null = null;
  private state: WorkerState = 'idle';
  private messageQueue: MainToWorkerMessage[] = [];
  private debug = false;
  private hasFlushedOnUnload = false;
  private hasRegisteredHandlers = false;
  private callbacks: WorkerClientCallbacks;
  private workerFactory: WorkerFactory;

  constructor(options: WorkerClientOptions) {
    this.callbacks = options.callbacks || {};
    this.workerFactory = options.workerFactory;
  }

  // ============================================
  // Public Methods
  // ============================================

  /**
   * Worker 초기화
   */
  init(config: LoggerConfig): boolean {
    if (this.state !== 'idle') {
      return this.state !== 'failed';
    }

    this.state = 'initializing';
    this.debug = config.debug || false;

    this.log('Initializing WorkerClient');

    try {
      this.worker = this.workerFactory();

      this.worker.onmessage = this.handleMessage;
      this.worker.onerror = this.handleError;

      // init 메시지는 post()를 거치지 않고 직접 전송
      // (post()는 state='ready'일 때만 즉시 전송, 그 외에는 큐에 넣음)
      this.worker.postMessage({ type: 'init', config });
      this.registerPageUnloadHandlers();
    } catch (error) {
      console.error('[WorkerClient] Failed to create worker:', error);
      this.state = 'failed';
      this.worker = null;
      this.callbacks.onError?.(
        error instanceof Error ? error.message : 'Worker creation failed',
        'WorkerClient.init',
      );
      return false;
    }

    return true;
  }

  /**
   * Worker에 메시지 전송 (큐잉 지원)
   */
  post(message: MainToWorkerMessage): boolean {
    if (this.state === 'ready' && this.worker) {
      this.worker.postMessage(message);
      return true;
    } else if (this.state !== 'failed') {
      this.enqueue(message);
      return true;
    }
    return false;
  }

  /**
   * Worker 큐에 남은 메시지 반환 (Worker 에러 시 SDK 경로로 복구용)
   */
  getQueuedMessages(): MainToWorkerMessage[] {
    return [...this.messageQueue];
  }

  /**
   * Worker 종료
   * 남은 로그를 Beacon으로 전송 후 Worker를 종료합니다.
   */
  terminate(): void {
    this.unregisterPageUnloadHandlers();

    if (this.worker) {
      // 남은 로그를 Beacon으로 즉시 전송 (Worker 종료 전)
      if (this.state === 'ready') {
        this.worker.postMessage({ type: 'flush_beacon' });
      }
      this.worker.postMessage({ type: 'destroy' });
      this.worker.terminate();
      this.worker = null;
    }

    this.state = 'idle';
    this.messageQueue = [];
    this.hasFlushedOnUnload = false;
  }

  // ============================================
  // Private Methods - Message Handling
  // ============================================

  private handleMessage = (event: MessageEvent<WorkerToMainMessage>) => {
    const message = event.data;

    switch (message.type) {
      case 'ready':
        this.log('Worker ready');
        this.state = 'ready';
        this.flushQueue();
        this.callbacks.onReady?.();
        break;

      case 'error':
        console.error(
          '[WorkerClient] Worker error:',
          message.error,
          message.context,
        );
        this.callbacks.onError?.(message.error, message.context);
        break;
    }
  };

  private handleError = (error: ErrorEvent) => {
    console.error('[WorkerClient] Worker error:', error);
    this.state = 'failed';
    this.worker = null;
    this.unregisterPageUnloadHandlers();
    this.callbacks.onError?.(error.message || 'Worker error');
  };

  // ============================================
  // Private Methods - Queue Management
  // ============================================

  private enqueue(message: MainToWorkerMessage): void {
    if (this.messageQueue.length >= MAX_QUEUE_LENGTH) {
      console.warn('[WorkerClient] Queue full, dropping oldest message');
      this.messageQueue.shift();
    }
    this.messageQueue.push(message);
  }

  private flushQueue(): void {
    if (!this.worker) return;

    this.messageQueue.forEach(message => {
      this.worker!.postMessage(message);
    });
    this.messageQueue = [];
  }

  // ============================================
  // Private Methods - Page Unload Handling
  // ============================================

  private flushOnUnload = () => {
    if (this.hasFlushedOnUnload) return;
    if (this.worker && this.state === 'ready') {
      this.hasFlushedOnUnload = true;
      this.worker.postMessage({ type: 'flush_beacon' });
    }
  };

  private handlePageHide = (_event: PageTransitionEvent) => {
    this.flushOnUnload();
  };

  private handleBeforeUnload = () => {
    this.flushOnUnload();
  };

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      if (this.worker && this.state === 'ready') {
        // iOS Safari에서는 pagehide/beforeunload가 발생하지 않을 수 있음
        // visibilitychange가 페이지 종료 전 마지막 이벤트일 수 있으므로 Beacon 사용
        if (isIOS()) {
          this.worker.postMessage({ type: 'flush_beacon' });
        } else {
          // 데스크톱에서는 일반 flush 사용 (pagehide/beforeunload가 더 신뢰성 있음)
          this.worker.postMessage({ type: 'flush' });
        }
      }
    }
  };

  private registerPageUnloadHandlers(): void {
    if (typeof window === 'undefined' || this.hasRegisteredHandlers) return;

    this.hasRegisteredHandlers = true;
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private unregisterPageUnloadHandlers(): void {
    if (typeof window === 'undefined' || !this.hasRegisteredHandlers) return;

    this.hasRegisteredHandlers = false;
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }

  // ============================================
  // Private Methods - Utility
  // ============================================

  private log(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[WorkerClient] ${message}`, ...args);
    }
  }
}
